import { Autocomplete, Card, InputAdornment } from "@mui/material";
import Grid from "@mui/material/Grid2";
import cep from "cep-promise";
import { useFormikContext } from "formik";
import React, { useRef } from "react";
import InputMaskWrapper from "@/components/atoms/InputMaskWrapper";

import FormField from "@/components/atoms/FormField";
import MDBox from "@/components/atoms/MDBox";
import MDInput from "@/components/atoms/MDInput";
import MDTypography from "@/components/atoms/MDTypography";
import { CustomerFormData } from "@/components/screens/CustomerFormPage/types";
import { COUNTRY_OPTIONS } from "@/constants/address.constants";
import { ADDRESS_MASK, CEP_LENGHT, STATE_UF_TRANSLATIONS } from "@/constants/documents.constants";
import { removeNonIntegersFromString } from "@/helpers/string-helpers";
import useLoading from "@/hooks/use-loading";
import { CustomerFormMessages } from "@/messages/customer/customer-form.messages";

type CustomerFormProps = {
  messages: CustomerFormMessages;
};

export default function AddressFields({ messages }: CustomerFormProps) {
  const formik = useFormikContext<CustomerFormData>();
  const { values, errors, handleChange, setFieldValue, setFieldError } = formik;

  const addressNumberRef = useRef<HTMLInputElement>(null);

  const {
    startLoading,
    endLoading,
    isLoading: isLoadingCEP,
    loadingIcon: loadingIconCEP,
  } = useLoading({ loadingIconColor: "primary" });

  const clearData = () => {
    setFieldValue("state", "");
    setFieldValue("stateUf", "");
    setFieldValue("city", "");
    setFieldValue("district", "");
    setFieldValue("address", "");
  };

  const handleCEPChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const {
      target: { value },
    } = e;
    const formattedValue = removeNonIntegersFromString(value);
    setFieldError("zipCode", undefined);
    setFieldValue("zipCode", formattedValue);
    const isCEPCompleted = formattedValue.length === CEP_LENGHT;
    if (!isCEPCompleted) clearData();
    if (isCEPCompleted) {
      startLoading();
      try {
        const cepData = await cep(formattedValue);
        const addressState = STATE_UF_TRANSLATIONS[cepData.state];
        setFieldValue("state", addressState);

        setFieldValue("stateUf", cepData.state);
        setFieldValue("city", cepData.city);
        setFieldValue("district", cepData.neighborhood);
        setFieldValue("address", cepData.street);
        setTimeout(() => {
          addressNumberRef.current?.focus();
        }, 0);
      } catch (err) {
        setFieldError("zipCode", messages.cepNotFoundError);
        clearData();
      }
      endLoading();
    }
  };

  const countryOptions = COUNTRY_OPTIONS(messages);
  const addressMask = ADDRESS_MASK(values.country);

  return (
    <Card>
      <MDBox p={3}>
        <MDBox display="flex" justifyContent="space-between">
          <MDTypography variant="h5" fontWeight="bold">
            {messages.addressSection}
          </MDTypography>
        </MDBox>

        <MDBox mt={1}>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, sm: 12, md: 4 }}>
              <InputMaskWrapper
                mask={addressMask}
                value={values.zipCode}
                onChange={(e) => handleCEPChange(e)}
                variant="standard"
                name="addressZipCode"
                label={messages.zipCodeLabel}
                fullWidth
                InputLabelProps={{
                  shrink: true,
                }}
                InputProps={{
                  ...(isLoadingCEP && {
                    endAdornment: (
                      <InputAdornment position="end">{loadingIconCEP}</InputAdornment>
                    ),
                  }),
                }}
                error={!!errors.zipCode}
                helperText={errors.zipCode}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 12, md: 4 }}>
              <FormField
                variant="standard"
                name="address"
                label={messages.addressLabel}
                value={values.address}
                onChange={handleChange}
                fullWidth
                InputLabelProps={{ shrink: true }}
                error={!!errors.address}
                helperText={errors.address}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 12, md: 4 }}>
              <FormField
                variant="standard"
                name="addressNumber"
                label={messages.addressNumberLabel}
                value={values.addressNumber}
                onChange={handleChange}
                fullWidth
                InputLabelProps={{ shrink: true }}
                error={!!errors.addressNumber}
                helperText={errors.addressNumber}
                inputRef={addressNumberRef}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 12, md: 4 }}>
              <FormField
                variant="standard"
                name="addressComp"
                label={messages.addressCompLabel}
                value={values.addressComp}
                onChange={handleChange}
                fullWidth
                InputLabelProps={{ shrink: true }}
                error={!!errors.addressComp}
                helperText={errors.addressComp}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 12, md: 4 }}>
              <FormField
                variant="standard"
                name="district"
                label={messages.districtLabel}
                value={values.district}
                onChange={handleChange}
                fullWidth
                InputLabelProps={{ shrink: true }}
                error={!!errors.district}
                helperText={errors.district}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 12, md: 4 }}>
              <FormField
                variant="standard"
                name="city"
                label={messages.cityLabel}
                value={values.city}
                onChange={handleChange}
                fullWidth
                InputLabelProps={{ shrink: true }}
                error={!!errors.city}
                helperText={errors.city}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 12, md: 4 }}>
              <FormField
                variant="standard"
                label={messages.stateLabel}
                name="state"
                value={values.state}
                fullWidth
                InputLabelProps={{ shrink: true }}
                error={!!errors.state}
                helperText={errors.state}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 12, md: 4 }}>
              <Autocomplete
                fullWidth
                options={countryOptions}
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={(option, value) => option.value === value.value}
                value={countryOptions.find((option) => option.value === values.country) || null}
                onChange={(_, newValue) => {
                  setFieldValue("country", newValue ? newValue.value : "");
                }}
                renderInput={(params) => (
                  <MDInput
                    {...params}
                    label={messages.countryLabel}
                    variant="standard"
                    error={!!errors.country}
                    helperText={errors.country}
                  />
                )}
              />
            </Grid>
          </Grid>
        </MDBox>
      </MDBox>
    </Card>
  );
}
